import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { FontIconName, TreeAccordion } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ActiveFilter } from "../common";
import { never } from "client_legacy/ndaw/errors";
import {
	ensureNotNil,
	isArray,
	isNotNil
} from "client/Misc/utils";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import {
	HubDataSource,
	HubItemHubSidebarNodeModel,
	RootHubSidebarNodeModel
} from "client/Api/Hub/hub";
import { HubItemSidebarSection } from "./HubItemSidebarSection";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import { KnowledgeItemSidebarSection } from "./KnowledgeItemSidebarSection";
import { hubItemKindToHubDataSourceMap } from "./common";
import { KnowledgeItemViewIdentifier } from "../KnowledgeItems/KnowledgeItemsPage";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { CommunitiesSidebarSection } from "./CommunitiesSidebarSection";

const DEFAULT_MAX_NUMBER_OF_ITEMS = 16;

export type HubSidebarProps = {
	rootNodes: readonly HubRootNode[];
	value: HubSidebarValue | undefined;
	activeFilter: ActiveFilter | null;
	onChange: (value: HubSidebarValue) => void;
	areActionsAvailable: boolean;
};

export const HubSidebar = (props: HubSidebarProps): ReactElement => {
	const {
		rootNodes,
		value,
		activeFilter,
		onChange,
		areActionsAvailable
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const initialAmountsToDisplayPerSource = useMemo(
		(): ReadonlyMap<HubDataSource, number> => {
			const kindsToDisplay: {
				source: HubDataSource;
				totalChildren: number;
				toDisplay: number;
			}[] = [];

			for (const node of rootNodes) {
				if (typeof node.source === "undefined") {
					continue;
				}

				const { source, children } = node;

				kindsToDisplay.push({
					source,
					toDisplay: 0,
					totalChildren: children.length,
				});
			}

			let remainingItems: number = DEFAULT_MAX_NUMBER_OF_ITEMS - kindsToDisplay.length;

			while (remainingItems > 0) {

				const kindsWithCapacity = kindsToDisplay.filter(
					item => item.toDisplay < item.totalChildren
				);

				if (kindsWithCapacity.length === 0) {
					break;
				}

				for (const item of kindsWithCapacity) {
					item.toDisplay++;
					remainingItems--;

					if (remainingItems <= 0) {
						break;
					}
				}
			}

			return fromArray(
				kindsToDisplay,
				(x) => x.source,
				(x) => x.toDisplay
			);
		},
		[rootNodes]
	);

	const shouldRenderRootItem = useCallback(
		(source: HubDataSource | undefined): boolean => {
			if (!activeFilter) {
				return true;
			}

			switch (activeFilter.kind) {
				case "author":
					return source === HubDataSource.Announcement
						|| source === HubDataSource.KnowledgeItem
						|| source === HubDataSource.Event;
				case "tag":
					return source === hubItemKindToHubDataSourceMap[activeFilter.value.itemKind];
				default:
					never(activeFilter);
			}
		},
		[activeFilter]
	);

	return (
		<TreeAccordion trackingId="hub-sidebar">
			{rootNodes
				.filter((x) => shouldRenderRootItem(x.source))
				.map((x): ReactElement => {
					switch (x.source) {
						case undefined:
							return (
								<TreeAccordion.Node key="overview">
									<TreeAccordion.Item
										expansionToggle="hidden"
										onClick={() => onChange({ source: undefined })}
										trackingId="overview-node"
										isSelected={isNotNil(value) && typeof value.source === "undefined"}
									>
										{localize("_Overview")}
									</TreeAccordion.Item>
								</TreeAccordion.Node>
							);
						case HubDataSource.KnowledgeItem:
							return (
								<KnowledgeItemSidebarSection
									key={HubDataSource.KnowledgeItem}
									selectedValue={value}
									item={x}
									collapsedItemsToDisplay={ensureNotNil(initialAmountsToDisplayPerSource.get(x.source))}
									activeFilter={activeFilter}
									onSelect={onChange}
									areActionsAvailable={areActionsAvailable}
									isMobile={isMobile}
								/>
							);
						case HubDataSource.Announcement:
						case HubDataSource.Event:
							return (
								<HubItemSidebarSection
									key={x.source}
									selectedValue={value}
									item={x}
									collapsedItemsToDisplay={ensureNotNil(initialAmountsToDisplayPerSource.get(x.source))}
									activeFilter={activeFilter}
									onSelect={onChange}
									areActionsAvailable={areActionsAvailable}
								/>
							);
						case HubDataSource.Community:
							return (
								<CommunitiesSidebarSection
									key={x.source}
									selectedValue={value}
									rootNode={x}
									defaultAmountOfItemsToDisplay={ensureNotNil(initialAmountsToDisplayPerSource.get(x.source))}
									onSelect={onChange}
								/>
							);
						default:
							return never(x);
					}
				})
			}
		</TreeAccordion>
	);
};

HubSidebar.displayName = "HubSidebar";

export type OverviewRootNode = {
	source: undefined;
	name: string;
};

export type HubItemGroupNode = {
	id: number;
	name: string;
	hasNewItems: boolean;
	children: readonly (HubItemCategoryNode | HubItemGroupNode)[];
};

export type HubItemCategoryNode = {
	name: string;
	newItemsCount: number;
} & (
		{
			id: number;
			isFollowing: boolean;
		}
		|
		{
			id: undefined;
		}
	);

export type HubItemRootNode = HubItemRootNodeBase & {
	source: HubDataSource.Announcement | HubDataSource.Event;
	children: readonly (HubItemCategoryNode | HubItemGroupNode)[];
};

export type CommunityNodeId = undefined | "my-feed" | number;

export type CommunityNode = {
	id: CommunityNodeId;
	name: string;
	newItemsCount?: number;
};

export type CommunityRootNode = {
	source: HubDataSource.Community;
	icon: FontIconName;
	name: string;
	children: readonly CommunityNode[];
	newItemsCount: number;
};

export type CommunityNodeValue = {
	source: HubDataSource.Community;
	communityId?: CommunityNodeId;
};

export type HubRootNode =
	| OverviewRootNode
	| HubItemRootNode
	| KnowledgeItemRootNode
	| CommunityRootNode;

export type OverviewValue = { source: undefined; };

export type HubItemCategoryValue = {
	source: HubDataSource.Announcement | HubDataSource.Event;
	categoryId?: number;
};

export type HubSidebarValue =
	| OverviewValue
	| HubItemCategoryValue
	| KnowledgeItemCategoryValue
	| CommunityNodeValue;

type HubItemRootNodeBase = {
	icon: FontIconName;
	name: string;
	newItemsCount: number;
};

export type KnowledgeItemGroupNode = {
	id: number;
	name: string;
	hasNewItems: boolean;
	children: readonly (KnowledgeItemCategoryNode | KnowledgeItemGroupNode)[];
};

export type KnowledgeItemRootNode = HubItemRootNodeBase & {
	source: HubDataSource.KnowledgeItem;
	children: readonly (KnowledgeItemCategoryNode | KnowledgeItemGroupNode)[];
};

export type KnowledgeItemCategoryValue = {
	source: HubDataSource.KnowledgeItem;
	viewIdentifier: KnowledgeItemViewIdentifier;
};

export type KnowledgeItemCategoryNode = {
	name: string;
	newItemsCount: number;
} & ({
		id: number;
		isFollowing: boolean;
} | {
	id: "all" | undefined;
});

export function convertNodeModelsToSidebarNodes(
	models: readonly RootHubSidebarNodeModel[]
): readonly HubRootNode[] {
	const result: HubRootNode[] = [];

	result.push({
		source: undefined,
		name: localize("_Overview")
	});

	for (const model of models) {
		switch (model.source) {
			case HubDataSource.Announcement:
			case HubDataSource.Event:
				{
					const {
						titleKey,
						icon,
						allKey
					} = kindToSectionConfigurationMapping[model.source];

					const children: (HubItemCategoryNode | HubItemGroupNode)[] = model.children
						.map(convertHubItemChildModel);

					const rootItemNewItemsCount: number = model.newItemsCount;

					children.unshift({
						id: undefined,
						name: localize(allKey),
						newItemsCount: rootItemNewItemsCount,
					});

					const root: HubItemRootNode = {
						source: model.source,
						icon,
						name: localize(titleKey),
						newItemsCount: rootItemNewItemsCount,
						children
					};

					result.push(root);
					break;
				}
			case HubDataSource.KnowledgeItem:
				{
					const {
						titleKey,
						icon,
						allKey
					} = kindToSectionConfigurationMapping[model.source];

					const children: (HubItemCategoryNode | HubItemGroupNode)[] = model.children
						.map(convertHubItemChildModel);

					const rootItemNewItemsCount: number = model.newItemsCount;

					const root: KnowledgeItemRootNode = {
						source: model.source,
						icon,
						name: localize(titleKey),
						newItemsCount: rootItemNewItemsCount,
						children: [
							{
								id: undefined,
								name: localize("_Overview"),
								newItemsCount: rootItemNewItemsCount,
							},
							{
								id: "all",
								name: localize(allKey),
								newItemsCount: rootItemNewItemsCount,
							},
							...children
						]
					};

					result.push(root);
					break;
				}
			case HubDataSource.Community:
				{
					const communities: readonly CommunityNode[] = model.children
						.map((x) => ({
							id: x.id,
							name: x.name
						}));

					const newItemsCount: number = model.newCommunityIds
						.length;

					result.push({
						source: HubDataSource.Community,
						icon: "users",
						name: localize("_Communities"),
						newItemsCount,
						children: [
							{
								id: "my-feed",
								name: localize("_MyFeed")
							},
							{
								id: undefined,
								name: localize("_DiscoverCommunities"),
								newItemsCount
							},
							...communities
						]
					});
					break;
				}
			default:
				never(model);
		}
	}

	return result;
}

function convertHubItemChildModel(model: HubItemHubSidebarNodeModel): HubItemCategoryNode | HubItemGroupNode {
	switch (model.kind) {
		case HubItemCategoryNodeKind.Group:
			const children: readonly (HubItemCategoryNode | HubItemGroupNode)[] = model
				.children
				.map(convertHubItemChildModel);

			const hasNewItems: boolean = children
				.reduce(
					(acc, cur) => acc
						|| (isHubItemGroupNode(cur)
							? cur.hasNewItems
							: cur.newItemsCount > 0
						),
					false
				);

			return {
				id: model.groupId,
				name: model.name,
				hasNewItems,
				children
			};
		case HubItemCategoryNodeKind.Category:
			const {
				id,
				name,
				isFollowing
			} = model.category;

			return {
				id,
				name,
				isFollowing,
				newItemsCount: model.newItemsCount
			};
		default:
			never(model);
	}
}

function isHubItemGroupNode(node: HubItemCategoryNode | HubItemGroupNode): node is HubItemGroupNode {
	return isArray((node as HubItemGroupNode).children);
}

type SectionConfiguration = {
	titleKey: LocalizationKey;
	icon: FontIconName;
	allKey: LocalizationKey;
};

const kindToSectionConfigurationMapping: { [key in HubItemKind]: SectionConfiguration; } = {
	[HubItemKind.Announcement]: {
		titleKey: "_Announcements",
		icon: "bullhorn",
		allKey: "_AllAnnouncements"
	},
	[HubItemKind.KnowledgeItem]: {
		titleKey: "_KnowledgeBase",
		icon: "book",
		allKey: "_AllCategories"
	},
	[HubItemKind.Event]: {
		titleKey: "_Events",
		icon: "calendar-star",
		allKey: "_AllEvents"
	}
};
